// Constants for the modeled §401(a)(4) cross-testing / general test.
//
// The general test (26 CFR 1.401(a)(4)-8) lets a defined-contribution allocation
// be tested "on the basis of benefits" by converting each allocation to an
// Equivalent Benefit Accrual Rate (EBAR): project the allocation to a testing age
// at a standard interest rate, convert it to a life annuity, and express that
// annuity as a percentage of compensation. The regulation permits a standard
// interest rate between 7.5% and 8.5% and a standard mortality table.
//
// These are documented MODELING ASSUMPTIONS, not the plan's actual actuarial
// elections. Crucially, the rate-group test and the EBAR-based average-benefit
// percentage test are RATIOS of EBARs, so the annuity-conversion factor cancels
// out of every pass/fail decision here; it affects only the displayed EBAR value.
export const CROSS_TEST_INTEREST_RATE = 0.085 // standard rate, top of the 7.5–8.5% range
export const CROSS_TEST_TESTING_AGE = 65
// Present value at the testing age of a $1/month life annuity (8.5%, UP-1984),
// used only to express EBAR as an annual benefit percent for display. UP-84 at
// 8.5% is the convention TPA proposal systems print (annual APR 7.9486), so
// displayed EBARs line up with the worksheets an actuary would produce.
export const CROSS_TEST_ANNUITY_FACTOR_MONTHLY = 95.3832 // = 7.9486 × 12

// Minimum allocation gateway (1.401(a)(4)-8(b)(1)(vi)): to test on a benefits
// basis, each NHCE must receive an allocation rate of at least the LESSER of 5%
// or one-third of the highest HCE allocation rate.
export const GATEWAY_MAX_PERCENT = 5
export const GATEWAY_FRACTION_OF_HIGHEST_HCE = 1 / 3

export const CROSS_TEST_CITATION = '26 CFR 1.401(a)(4)-2, -3, -8'

// ---- Imputed permitted disparity (§1.401(a)(4)-7), opt-in TPA election ----
// Adjusts allocation rates for the Social Security disparity a plan is permitted
// to build in, so equivalent designs are not penalized in the rate-group test.
// Never applied to the minimum-allocation gateway. Modeled: the taxable wage
// base is the §401(l) integration level and the disparity factor is the 5.7%
// allocation limit; a plan's actual permitted disparity depends on its adopted
// integration level and prior use, which this model cannot verify.
export const PERMITTED_DISPARITY_FACTOR = 0.057
// Old-Age/Survivors/Disability (OASDI) contribution and benefit base by year.
// Used only as the integration level for imputed permitted disparity.
export const TAXABLE_WAGE_BASE_BY_YEAR = { 2025: 176100, 2026: 183600 }
export const PERMITTED_DISPARITY_CITATION = '26 CFR 1.401(a)(4)-7'

// ---- Rate banding / grouping (§1.401(a)(4)-2(c)(2)(v), -3(d)(3)(ii)), opt-in ----
// When forming rate groups, the employer may pick midpoint rates and treat all
// rates within a band AROUND that midpoint as a single (midpoint) rate. Two
// rules, and the employer may use whichever gives the LARGER band:
//   - Multiplicative: the band spans [midpoint × (1 − f), midpoint × (1 + f)]
//     with f = RATE_BAND_FRACTION (5%).
//   - Additive (rates as % of comp): the band spans midpoint ± RATE_BAND_ADDITIVE_PP
//     percentage points (1/20 of a percentage point).
// For a rate group anchored on an HCE at rate H, the most favorable midpoint
// puts H at the top of a band, so an employee is deemed to share H's rate when
// their rate is at least the band FLOOR:
//   multiplicative floor = H × (1 − f)/(1 + f);  additive floor = H − 2 × pp.
// Grouping is barred where HCE rates within a band are significantly higher than
// NHCE rates in it (anti-abuse); that is facts-and-circumstances, so the model
// applies the band and discloses the widest HCE-over-NHCE spread for review.
export const RATE_BAND_FRACTION = 0.05
export const RATE_BAND_ADDITIVE_PP = 0.05
export const RATE_BANDING_CITATION = '26 CFR 1.401(a)(4)-2(c)(2)(v), -3(d)(3)(ii)'
